import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from 'contentful'
import type { TypeBlogPageSkeleton } from './TypeBlogPage'
import type { TypeComponentCardSkeleton } from './TypeComponentCard'
import type { TypeComponentCodeTabsSkeleton } from './TypeComponentCodeTabs'
import type { TypeComponentCtaLinkSkeleton } from './TypeComponentCtaLink'
import type { TypeComponentHubspotCtaSkeleton } from './TypeComponentHubspotCta'
import type { TypeComponentPricingSkeleton } from './TypeComponentPricing'
import type { TypeLandingPageSkeleton } from './TypeLandingPage'
import type { TypeNt_experienceSkeleton } from './TypeNt_experience'
import type { TypeResourceImageSkeleton } from './TypeResourceImage'
import type { TypeResourcePageSkeleton } from './TypeResourcePage'
import type { TypeResourceVideoPageSkeleton } from './TypeResourceVideoPage'

export interface TypeSectionSetOfCardsFields {
  internalName: EntryFieldTypes.Symbol
  eyebrowText?: EntryFieldTypes.Symbol
  headline?: EntryFieldTypes.Symbol
  anchorLinkTitle?: EntryFieldTypes.Symbol
  body?: EntryFieldTypes.RichText
  backgroundColor?: EntryFieldTypes.Symbol<'Indigo Blue' | 'Light Gray' | 'Primary Blue' | 'Transparent' | 'White'>
  unconstrained?: EntryFieldTypes.Boolean
  dropShadow?: EntryFieldTypes.Boolean
  heroLayout?: EntryFieldTypes.Symbol<'Horizontal' | 'Vertical'>
  heroBackgroundColor?: EntryFieldTypes.Symbol<
    'Arctic' | 'Atmosphere' | 'Dusk' | 'Horizon' | 'Midnight' | 'Sea' | 'Space' | 'Transparent' | 'White'
  >
  transparentCards?: EntryFieldTypes.Boolean
  cards?: EntryFieldTypes.Array<
    EntryFieldTypes.EntryLink<
      | TypeBlogPageSkeleton
      | TypeComponentCardSkeleton
      | TypeComponentCodeTabsSkeleton
      | TypeComponentPricingSkeleton
      | TypeLandingPageSkeleton
      | TypeResourceImageSkeleton
      | TypeResourcePageSkeleton
      | TypeResourceVideoPageSkeleton
    >
  >
  imageType?: EntryFieldTypes.Symbol<'Icon' | 'Image' | 'Spot'>
  cardIconSize?: EntryFieldTypes.Symbol<'1/2' | '1/3' | '1/4' | '2/3' | '3/4' | 'Full' | 'Unconstrained'>
  cardIconLocation: EntryFieldTypes.Symbol<'Left' | 'Right'>
  stackedAssetLocation?: EntryFieldTypes.Symbol<'Center' | 'Left'>
  stackImageOverText?: EntryFieldTypes.Boolean
  cardsPerColumn?: EntryFieldTypes.Integer<1 | 2 | 3 | 4 | 5 | 6>
  actions?: EntryFieldTypes.Array<
    EntryFieldTypes.EntryLink<TypeComponentCtaLinkSkeleton | TypeComponentHubspotCtaSkeleton>
  >
  actionsPosition?: EntryFieldTypes.Symbol<'Above cards' | 'Below cards'>
  alignContent?: EntryFieldTypes.Boolean
  nt_experiences?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeNt_experienceSkeleton>>
  excludeFromPdf?: EntryFieldTypes.Boolean
}

export type TypeSectionSetOfCardsSkeleton = EntrySkeletonType<TypeSectionSetOfCardsFields, 'sectionSetOfCards'>
export type TypeSectionSetOfCards<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<
  TypeSectionSetOfCardsSkeleton,
  Modifiers,
  Locales
>

export function isTypeSectionSetOfCards<Modifiers extends ChainModifiers, Locales extends LocaleCode>(
  entry?: Entry<EntrySkeletonType, Modifiers, Locales> | undefined
): entry is TypeSectionSetOfCards<Modifiers, Locales> {
  return entry?.sys?.contentType?.sys?.id === 'sectionSetOfCards'
}
